import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import { walletAPI, paymentErrorText, serverMessage } from '../../services/walletApi';
import { openCheckout } from '../../services/payments/razorpayCheckout';
import { isCheckoutCancelled } from '../../services/payments/razorpayOptions';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { Logger } from '../../utils/logger';
import { formatLocalDateOnly, formatLocalDateTime } from '../../utils/dateUtils';
import {
  firstName,
  payBasket,
  payStartBody,
  tickedByDefault,
  topUpChoices,
  topUpProblem,
  walletMoney,
} from '../../utils/walletFormat';
import type { PayAndTopUp, PayChild, PayResult } from '../../types/wallet';
import { BusyOverlay, Notice } from './WalletBits';

/**
 * Pay & top up (parent portal, owner 15 Oct 2026), shared by web and native.
 *
 * Every child's unpaid fees and wallet bills, plus a top-up for each wallet,
 * ticked here and paid in ONE payment. Only where the school has a payment
 * gateway (onlinePayAvailable) is there a Pay button; elsewhere the same list
 * is paid at the school office, quoting each child's Payer ID. The server
 * re-prices everything; this screen only says what was ticked.
 */
export function PayAndTopUpContent({ onPaid }: { onPaid?: () => void }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  const [data, setData] = useState<PayAndTopUp | null>(null);
  const [ticked, setTicked] = useState<Record<string, boolean>>({});
  const [topUps, setTopUps] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [result, setResult] = useState<PayResult | null>(null);
  const [pendingBasket, setPendingBasket] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await walletAPI.getPayAndTopUp();
      setData(d);
      const now = Date.now();
      setTicked(Object.fromEntries(d.children.flatMap(c => c.items.map(i => [i.invoiceId, tickedByDefault(i, now)]))));
      setTopUps({});
      setError(null);
    } catch (err) {
      Logger.error('Wallet', 'Failed to load Pay & top up', err);
      setError(serverMessage(err, 'What you owe could not be loaded. Try again in a moment.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const basket = useMemo(() => payBasket(data, ticked, topUps), [data, ticked, topUps]);
  const cur = data?.currency ?? 'USD';

  /** The settled result comes from GET /wallet/my/pay/{basketId}; the confirm reply stands in if that read fails. */
  const readResult = async (basketId: string, fallback?: PayResult) => {
    try {
      return await walletAPI.getPayResult(basketId);
    } catch {
      return fallback ?? null;
    }
  };

  const pay = async () => {
    if (!data || busy) { return; }
    setError(null);
    setNotice(null);
    let basketId: string | null = null;
    try {
      setBusy(true);
      const started = await walletAPI.startPay(payStartBody(data, ticked, topUps));
      basketId = started.basketId;
      // The loader steps aside while the gateway's own sheet is open.
      setBusy(false);
      const callback = await openCheckout(started.provider, started.checkoutPayload, 'Fees and wallet top-ups');
      setBusy(true);
      setPendingBasket(basketId);
      const confirmed = await walletAPI.confirmPay(started.basketId, callback);
      const done = await readResult(started.basketId, confirmed);
      if (done) {
        setResult(done);
        setPendingBasket(null);
      }
      onPaid?.();
    } catch (err) {
      if (isCheckoutCancelled(err)) {
        setNotice('Payment cancelled - nothing was taken.');
      } else {
        // The webhook settles a paid basket on its own if this screen never hears back.
        setError(basketId
          ? paymentErrorText(err, 'The payment did not finish here. If money was taken, everything will be applied shortly - check again in a minute.')
          : paymentErrorText(err, 'The payment could not be started.'));
      }
    } finally {
      setBusy(false);
    }
  };

  const checkAgain = async () => {
    if (!pendingBasket) { return; }
    setBusy(true);
    try {
      const r = await walletAPI.getPayResult(pendingBasket);
      if (r.status === 'Settled' || r.status === 'NeedsAttention') {
        setResult(r);
        setPendingBasket(null);
        setError(null);
        onPaid?.();
      } else {
        setNotice(r.message ?? 'Still waiting for the payment to complete.');
      }
    } catch (err) {
      setError(serverMessage(err, 'The payment could not be checked.'));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return <PaidView result={result} styles={styles} colors={colors} onDone={() => { setResult(null); load(); }} />;
  }

  if (loading && !data) { return <LoadingSpinner />; }

  const online = !!data?.onlinePayAvailable;
  const canPay = basket.total > 0 && basket.problems === 0 && !busy;

  return (
    <View testID="pay-and-top-up">
      <Text style={styles.lede}>Tick what to pay and add money to the wallets - one payment covers it all.</Text>

      {error && (
        <Notice tone="error" onClose={() => setError(null)}>
          <Text style={styles.noticeText}>{error}</Text>
          {pendingBasket && (
            <TouchableOpacity onPress={checkAgain} style={styles.linkBtn} testID="pay-check-again">
              <Text style={styles.linkBtnText}>Check again</Text>
            </TouchableOpacity>
          )}
        </Notice>
      )}
      {notice && <Notice onClose={() => setNotice(null)}>{notice}</Notice>}

      {data && data.children.length === 0 && (
        <Notice>No children are linked to your login. Ask the school office to link them.</Notice>
      )}

      {data && data.children.map(c => (
        <ChildCard
          key={c.studentId}
          child={c}
          data={data}
          styles={styles}
          colors={colors}
          ticked={ticked}
          onTick={(id, on) => setTicked(t => ({ ...t, [id]: on }))}
          topUp={topUps[c.studentId] ?? ''}
          onTopUp={v => setTopUps(t => ({ ...t, [c.studentId]: v }))}
        />
      ))}

      {data && data.children.length > 0 && (
        <View style={styles.summary}>
          <Text style={styles.summaryTitle}>This payment</Text>
          {basket.feeCount > 0 && (
            <View style={styles.sumRow}>
              <Text style={styles.sumLabel}>Fees · {basket.feeCount} {basket.feeCount === 1 ? 'item' : 'items'}</Text>
              <Text style={styles.sumValue}>{walletMoney(basket.fees, cur)}</Text>
            </View>
          )}
          {basket.bills.map((b, i) => (
            <View key={i} style={styles.sumRow}>
              <Text style={styles.sumLabel}>Wallet bill · {b.name}</Text>
              <Text style={styles.sumValue}>{walletMoney(b.amount, cur)}</Text>
            </View>
          ))}
          {basket.topUpCount > 0 && (
            <View style={styles.sumRow}>
              <Text style={styles.sumLabel}>Wallet top-ups · {basket.topUpCount}</Text>
              <Text style={styles.sumValue}>{walletMoney(basket.topUps, cur)}</Text>
            </View>
          )}
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Total</Text>
            <Text style={styles.totalValue} testID="pay-total">{walletMoney(basket.total, cur)}</Text>
          </View>

          {online ? (
            <>
              <TouchableOpacity
                style={[styles.payBtn, !canPay && { opacity: 0.5 }]}
                onPress={pay}
                disabled={!canPay}
                testID="pay-now"
              >
                <Text style={styles.payBtnText}>{`Pay ${walletMoney(basket.total, cur)}`}</Text>
              </TouchableOpacity>
              <Text style={styles.fine}>
                Card, UPI or net banking. Each fee and bill is marked paid and the wallets are topped up as soon as the
                payment is confirmed - even if this screen is closed.
              </Text>
            </>
          ) : (
            <View style={styles.officeBox} testID="pay-at-office">
              <Text style={styles.officeTitle}>Pay at the school office</Text>
              <Text style={styles.officeText}>
                Bring this list to the office. They take it at the counter in one go, by cash or card. Quote the Payer ID:
              </Text>
              {data.children.filter(c => c.payerId).map(c => (
                <View key={c.studentId} style={styles.officeIdRow}>
                  <Text style={styles.officeText}>{c.name}</Text>
                  <Text style={styles.payerMono}>{c.payerId}</Text>
                </View>
              ))}
            </View>
          )}
          {basket.bills.length > 0 && (
            <Text style={styles.fine}>Unpaid wallet bills are ticked first: a top-up would pay them anyway.</Text>
          )}
        </View>
      )}

      <BusyOverlay visible={busy || (loading && !!data)} />
    </View>
  );
}

function ChildCard({ child, data, styles, colors, ticked, onTick, topUp, onTopUp }: {
  child: PayChild;
  data: PayAndTopUp;
  styles: ReturnType<typeof createStyles>;
  colors: ThemeColors;
  ticked: Record<string, boolean>;
  onTick: (invoiceId: string, on: boolean) => void;
  topUp: string;
  onTopUp: (value: string) => void;
}) {
  const cur = data.currency;
  const canTopUp = data.walletsEnabled && !!child.walletId && child.walletStatus === 'Active';
  const problem = topUpProblem(topUp, child.walletBalance, data.minTopUp, data.maxBalance, cur);

  return (
    <View style={styles.child} testID={`pay-child-${child.studentId}`}>
      <View style={styles.childHead}>
        <View style={{ flex: 1 }}>
          <Text style={styles.childName}>{child.name}</Text>
          <Text style={styles.childMeta}>
            {[child.className, child.payerId ? `Payer ID ${child.payerId}` : null].filter(Boolean).join(' · ')}
          </Text>
        </View>
        {child.walletId && (
          <Text style={styles.holds}>Wallet holds{'\n'}<Text style={styles.holdsValue}>{walletMoney(child.walletBalance, cur)}</Text></Text>
        )}
      </View>

      {child.items.length === 0 && <Text style={styles.nothing}>Nothing to pay.</Text>}
      {child.items.map(i => {
        const on = !!ticked[i.invoiceId];
        return (
          <TouchableOpacity
            key={i.invoiceId}
            style={styles.line}
            onPress={() => onTick(i.invoiceId, !on)}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: on }}
            testID={`pay-item-${i.invoiceId}`}
          >
            <View style={[styles.checkbox, on && { backgroundColor: colors.primary, borderColor: colors.primary }]}>
              {on && <FeatherIcon name="check" size={13} color="#FFF" />}
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.lineWhat}>
                {i.description}
                {i.kind === 'WALLET_BILL' ? '  · Wallet bill' : ''}
              </Text>
              <Text style={[styles.lineSub, i.overdue && { color: colors.danger }]}>
                {[i.reference, i.dueDate ? `${i.overdue ? 'Was due' : 'Due'} ${formatLocalDateOnly(i.dueDate)}` : null].filter(Boolean).join(' · ')}
              </Text>
            </View>
            <Text style={styles.amount}>{walletMoney(i.amountDue, cur)}</Text>
          </TouchableOpacity>
        );
      })}

      {canTopUp && (
        <View style={styles.topUp}>
          <Text style={styles.topUpLabel}>Add to {firstName(child.name)}'s wallet</Text>
          <View style={styles.chipRow}>
            {topUpChoices(data.minTopUp).map(v => {
              const on = Number(topUp) === v;
              return (
                <TouchableOpacity
                  key={v}
                  style={[styles.amountChip, on && { backgroundColor: colors.primary, borderColor: colors.primary }]}
                  onPress={() => onTopUp(on ? '' : String(v))}
                  accessibilityState={{ selected: on }}
                >
                  <Text style={[styles.amountChipText, on && { color: '#FFF' }]}>{walletMoney(v, cur)}</Text>
                </TouchableOpacity>
              );
            })}
            <TextInput
              style={styles.topUpInput}
              value={topUp}
              onChangeText={v => onTopUp(v.replace(/[^0-9.]/g, ''))}
              keyboardType="decimal-pad"
              placeholder="Other"
              placeholderTextColor={colors.textMuted}
              accessibilityLabel={`Top-up for ${child.name} (${cur})`}
              testID={`pay-topup-${child.studentId}`}
            />
          </View>
          {problem && <Text style={styles.fieldError}>{problem}</Text>}
        </View>
      )}
    </View>
  );
}

function PaidView({ result, styles, colors, onDone }: {
  result: PayResult;
  styles: ReturnType<typeof createStyles>;
  colors: ThemeColors;
  onDone: () => void;
}) {
  const cur = result.currency;
  const ok = result.status === 'Settled';
  return (
    <View style={styles.summary} testID="pay-result">
      <View style={styles.paidHead}>
        <View style={[styles.paidTick, { backgroundColor: ok ? colors.successLight : colors.warningLight }]}>
          <FeatherIcon name={ok ? 'check' : 'alert-circle'} size={22} color={ok ? colors.success : colors.warning} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.summaryTitle}>{ok ? `Paid ${walletMoney(result.amount, cur)}` : `Received ${walletMoney(result.amount, cur)}`}</Text>
          <Text style={styles.lineSub}>
            {[result.gatewayPaymentId, result.receiptNumber && `receipt ${result.receiptNumber}`, result.paidAt && formatLocalDateTime(result.paidAt)]
              .filter(Boolean).join(' · ')}
          </Text>
        </View>
      </View>
      {result.message && <Notice tone="warn">{result.message}</Notice>}
      <Text style={styles.eyebrow}>How it was split</Text>
      {result.lines.map((l, i) => (
        <View key={i} style={styles.line}>
          <View style={{ flex: 1 }}>
            <Text style={styles.lineWhat}>
              {firstName(l.childName)} · {l.kind === 'WALLET_TOPUP' ? 'Wallet top-up' : l.description}
            </Text>
            <Text style={styles.lineSub}>
              {[l.reference, l.walletBalanceAfter != null ? `balance now ${walletMoney(l.walletBalanceAfter, cur)}` : null,
                l.state === 'NeedsAttention' ? 'With the office' : l.state].filter(Boolean).join(' · ')}
            </Text>
          </View>
          <Text style={styles.amount}>{walletMoney(l.amount, cur)}</Text>
        </View>
      ))}
      <View style={styles.totalRow}>
        <Text style={styles.totalLabel}>Total</Text>
        <Text style={styles.totalValue}>{walletMoney(result.amount, cur)}</Text>
      </View>
      <TouchableOpacity style={styles.payBtn} onPress={onDone} testID="pay-done">
        <Text style={styles.payBtnText}>Done</Text>
      </TouchableOpacity>
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  lede: { fontSize: 12.5, color: colors.textSecondary, marginBottom: 12 },
  noticeText: { fontSize: 12.5, lineHeight: 18, color: colors.textPrimary },
  linkBtn: { marginTop: 6, alignSelf: 'flex-start' },
  linkBtnText: { fontSize: 12.5, fontWeight: '800', color: colors.primary },
  child: { backgroundColor: colors.card, borderRadius: 14, borderWidth: 1, borderColor: colors.divider, padding: 14, marginBottom: 12 },
  childHead: { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
  childName: { fontSize: 14.5, fontWeight: '800', color: colors.textPrimary },
  childMeta: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
  holds: { fontSize: 11, color: colors.textSecondary, textAlign: 'right' },
  holdsValue: { fontSize: 13.5, fontWeight: '800', color: colors.textPrimary },
  nothing: { fontSize: 12.5, color: colors.textMuted, paddingVertical: 8 },
  line: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: colors.divider },
  checkbox: { width: 20, height: 20, borderRadius: 5, borderWidth: 1.5, borderColor: colors.border, alignItems: 'center', justifyContent: 'center' },
  lineWhat: { fontSize: 13, fontWeight: '700', color: colors.textPrimary },
  lineSub: { fontSize: 11.5, color: colors.textSecondary, marginTop: 2 },
  amount: { fontSize: 13.5, fontWeight: '800', color: colors.textPrimary },
  topUp: { marginTop: 10 },
  topUpLabel: { fontSize: 12.5, fontWeight: '700', color: colors.textSecondary, marginBottom: 8 },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
  amountChip: { borderWidth: 1, borderColor: colors.border, borderRadius: 999, paddingHorizontal: 12, paddingVertical: 7, backgroundColor: colors.surface },
  amountChipText: { fontSize: 12.5, fontWeight: '700', color: colors.textPrimary },
  topUpInput: { minWidth: 90, borderWidth: 1, borderColor: colors.border, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 7, fontSize: 13, color: colors.textPrimary, backgroundColor: colors.surface },
  fieldError: { fontSize: 12, color: colors.danger, marginTop: 6, fontWeight: '600' },
  summary: { backgroundColor: colors.card, borderRadius: 14, borderWidth: 1, borderColor: colors.divider, padding: 16, marginBottom: 12 },
  summaryTitle: { fontSize: 16, fontWeight: '800', color: colors.textPrimary, marginBottom: 8 },
  sumRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 },
  sumLabel: { fontSize: 13, color: colors.textSecondary },
  sumValue: { fontSize: 13, fontWeight: '700', color: colors.textPrimary },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', borderTopWidth: 1, borderTopColor: colors.divider, marginTop: 8, paddingTop: 10 },
  totalLabel: { fontSize: 13.5, fontWeight: '700', color: colors.textSecondary },
  totalValue: { fontSize: 20, fontWeight: '800', color: colors.textPrimary },
  payBtn: { backgroundColor: colors.primary, borderRadius: 10, paddingVertical: 13, alignItems: 'center', marginTop: 14 },
  payBtnText: { color: '#FFF', fontSize: 14, fontWeight: '800' },
  fine: { fontSize: 11.5, color: colors.textSecondary, marginTop: 8, lineHeight: 16 },
  officeBox: { backgroundColor: colors.warningLight, borderRadius: 12, padding: 14, marginTop: 14 },
  officeTitle: { fontSize: 13.5, fontWeight: '800', color: colors.textPrimary, marginBottom: 4 },
  officeText: { fontSize: 12.5, lineHeight: 18, color: colors.textPrimary },
  officeIdRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 },
  payerMono: { fontSize: 13, fontWeight: '800', color: colors.textPrimary, fontFamily: 'monospace' },
  paidHead: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 8 },
  paidTick: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  eyebrow: { fontSize: 11, fontWeight: '800', color: colors.textMuted, letterSpacing: 0.6, textTransform: 'uppercase', marginTop: 6 },
});
